import { TaskStatus, GroupedTasks as GroupedTasks, TaskArgument, ChallengeCampaign, ChallengeTask, Campaign, CampaignType } from "../types";

export const getAirdropHeaderProjects = (campaigns: ChallengeCampaign[]) => {
  return campaigns.map((campaign) => {
    return {
      img: campaign.metadata.projectImage,
      id: campaign.id,
    };
  });
};

// Task types to exclude from main quests page
export const EXCLUDED_TASK_TYPES = ["Devnet Challenges"];

export const getActiveTasks = (campaign: ChallengeCampaign, tasks: TaskStatus[], excludeTypes: string[] = []) => {
  const activeTasks = campaign?.tasks?.filter((task) => task.isEnabled && task.isUiEnabled);

  const activeTasksStatus = tasks.filter((task) => {
    const isActive = activeTasks?.some((t) => t.id === task.taskGuid);
    const isExcluded = excludeTypes.includes(task.type);
    return isActive && !isExcluded;
  });

  // Create a map of taskGuid to displayOrder for sorting
  const displayOrderMap = new Map<string, number>();
  activeTasks?.forEach((task) => {
    displayOrderMap.set(task.id, task.displayOrder);
  });

  // Sort the active tasks by displayOrder (ascending - lowest first)
  const sortedActiveTasksStatus = activeTasksStatus.sort((a, b) => {
    const orderA = displayOrderMap.get(a.taskGuid) ?? Number.MAX_SAFE_INTEGER;
    const orderB = displayOrderMap.get(b.taskGuid) ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });

  return sortedActiveTasksStatus;
};

export const groupTasksByType = (activeTasksStatus: TaskStatus[]) => {
  const taskTypeOrder = [
    "Elitra",
    "Brokex",
    "On-Chain Challenges",
    "Create spicy content",
    "Daily challenge",
    "Reactor Quests",
    "Game Challenges",
    "Special Challenges",
    "Quiz Challenges",
    "Spread the word",
  ];

  const groupedTasksArray = Object.values(
    activeTasksStatus?.reduce(
      (acc, task) => {
        const taskTypeIndex = taskTypeOrder.indexOf(task.type);
        if (taskTypeIndex !== -1) {
          if (acc[taskTypeIndex]) {
            acc[taskTypeIndex].push(task);
          } else {
            acc[taskTypeIndex] = [task];
          }
        }
        return acc;
      },
      {} as Record<number, TaskStatus[]>,
    ) ?? {},
  );

  // Sort each group to place UI-disabled tasks (isUiEnabled: false) at the end
  const sortedGroupedTasksArray = groupedTasksArray.map((group) => {
    return group.sort((a, b) => {
      // Tasks with isUiEnabled: false should come last
      const aDisabled = a.isUiEnabled === false ? 1 : 0;
      const bDisabled = b.isUiEnabled === false ? 1 : 0;
      return aDisabled - bDisabled;
    });
  });

  return sortedGroupedTasksArray;
};

export const groupAndFilterByActiveTasks = (
  campaign: ChallengeCampaign,
  tasks: TaskStatus[],
  excludeTypes: string[] = EXCLUDED_TASK_TYPES,
): GroupedTasks => {
  return groupTasksByType(getActiveTasks(campaign, tasks, excludeTypes));
};

// export const airdropsClasses = (airdropClasses: IAirdropClasses) => {
//   const classes = [
//     {
//       title: airdropClasses?.project.title,
//       description: airdropClasses?.project.shortDescription,
//       children: <Image src={''} alt={'Project Image'} width={0} height={0} />,
//       points: airdropClasses?.project.points,
//       moreInfoContent: airdropClasses?.project.description,
//       icon: airdropClasses?.project.icon,
//     },
//     {
//       title: airdropClasses?.partners.title,
//       description: airdropClasses?.partners.shortDescription,

//       points: airdropClasses?.partners.points,
//       moreInfoContent: airdropClasses?.partners.description,
//       icon: airdropClasses?.partners.icon,
//     },
//     {
//       title: airdropClasses?.ecosystem.title,
//       description: airdropClasses?.ecosystem.shortDescription,
//       points: airdropClasses?.ecosystem.points,
//       moreInfoContent: airdropClasses?.ecosystem.description,
//       icon: airdropClasses?.ecosystem.icon,
//     },
//   ];

//   return classes;
// };

export const setTaskGuidAndName = (taskGuid: string, taskName: string) => {
  window.localStorage.setItem("taskGuid", taskGuid);
  window.localStorage.setItem("taskName", taskName);
};

export const consumeTaskGuidAndName = () => {
  const taskGuid = window.localStorage.getItem("taskGuid");
  const taskName = window.localStorage.getItem("taskName");

  window.localStorage.removeItem("taskGuid");
  window.localStorage.removeItem("taskName");
  return { taskGuid, taskName };
};

export const getArgument = (args: TaskArgument[], argumentName: string): TaskArgument | undefined => {
  const argument = args.find((arg) => arg.name === argumentName);
  if (!argument || !argument.value) {
    return undefined;
  }

  return argument;
};

export const getAirdropArgumentValue = (args: TaskArgument[], argumentName: string): string | undefined => {
  const argument = getArgument(args, argumentName);
  return argument?.value;
};

export const getMaxPoints = (campaignTask: ChallengeTask) => {
  let maxPoints: number = Number(campaignTask.points);

  const parsedTiers = JSON.parse(getAirdropArgumentValue(campaignTask.arguments, "pointsTiers") || "{}");

  const maxTierKey = Object.keys(parsedTiers).sort((a, b) => Number(b) - Number(a))[0] || "0";

  const maxTierPoints = parsedTiers[maxTierKey] || 0;

  if (/^\d+$/.test(maxTierPoints?.toString()) && maxTierPoints > 0) {
    maxPoints = Number(maxTierPoints);
  }

  return maxPoints;
};

export const getInstancesTimeframe = (campaignTask: ChallengeTask): "Daily" | "Weekly" | "Monthly" | null => {
  const instances = JSON.parse(getAirdropArgumentValue(campaignTask.arguments, "instances") || "[]") as {
    startTime: string;
    endTime: string;
  }[];
  console.log("instances for ", campaignTask.title, instances);

  if (!instances || instances.length < 2) {
    console.log("no instances for ", campaignTask.title);
    return null;
  }

  // Get the time difference between two consecutive instances in milliseconds
  if (!instances[0] || !instances[1]) return null;
  const firstInstance = new Date(instances[0].startTime);
  const secondInstance = new Date(instances[1].startTime);
  const timeDifference = secondInstance.getTime() - firstInstance.getTime();

  // Convert milliseconds to days
  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);
  console.log("daysDifference", daysDifference);

  if (daysDifference <= 1) return "Daily";
  if (daysDifference <= 7) return "Weekly";
  return "Monthly";
};

export const getCampaignIdAndGroupId = (campaignId: string): [string, string | undefined] => {
  const parts = campaignId.split("-");
  if (parts.length === 1) {
    return [parts[0]!, undefined];
  }
  const groupId = parts.pop(); // Get the last segment as groupId
  const questId = parts.join("-"); // Join remaining segments with '-'
  return [questId, groupId];
};

export const getCampaignFromChallengeType = (campaigns: ChallengeCampaign[]): Campaign[] => {
  console.log(campaigns);
  return campaigns.map((campaign) => {
    return {
      id: campaign.id,
      type: CampaignType.CHALLENGE,
      campaignGroup: campaign.campaignGroup,
      name: campaign.metadata?.name,
      description: campaign.metadata?.description,
      imageUrl: campaign.metadata?.imageUrl,
      startDate: campaign.startDate,
      endDate: campaign.endDate,
      allocation: campaign.metadata?.totalAmount,
      token: campaign.metadata?.token,
      totalUsers: campaign.metadata?.totalUsers,
      topUsers: campaign.metadata?.topUsers,
      chainId: campaign.chainId,
      totalQuests: campaign.metadata?.totalQuests,
      isEnabled: campaign.isEnabled,
      tokenImageUrl: campaign.metadata?.token,
      tokenSymbol: campaign.metadata?.tokenSymbol,
    };
  });
};

/**
 * Check if a task was created within the last 12 hours
 * @param createdAt - The creation date of the task (string or Date)
 * @returns true if the task is less than 12 hours old, false otherwise
 */
export const isTaskNew = (createdAt?: string | Date): boolean => {
  if (!createdAt) return false;

  const createdDate = typeof createdAt === "string" ? new Date(createdAt) : createdAt;
  const now = new Date();
  const twelveHoursInMs = 12 * 60 * 60 * 1000;

  return now.getTime() - createdDate.getTime() < twelveHoursInMs;
};

/**
 * Check if today is a bonus day for daily claim tasks (January 9th UTC)
 * On this day, daily claim gives 50 points instead of 1
 * @returns true if today is January 9th UTC
 */
export const isBonusDay = (): boolean => {
  const now = new Date();
  return now.getUTCMonth() === 0 && now.getUTCDate() === 9;
};

/**
 * Check if a task is a daily checkin/recurring task
 * @param taskId - The task type/id to check
 * @returns true if the task is a daily checkin task
 */
export const isDailyCheckinTask = (taskId?: string): boolean => {
  if (!taskId) return false;
  return taskId === "daily_checkin" || taskId === "recurring_task";
};
